/**
 * @fileoverview Zod schemas for Shopify product import API.
 * @module features/shopify-import/schemas/shopifyImportSchema
 */

import { z } from 'zod'

/**
 * Shopify store available for product import (DIRECT connection mode only).
 */
export const ShopifyStoreSchema = z.object({
  connectionId: z.string().uuid(),
  name: z.string(),
  domain: z.string(),
  connectionMode: z.string(),
})

export type ShopifyStore = z.infer<typeof ShopifyStoreSchema>

export const ShopifyStoreArraySchema = z.array(ShopifyStoreSchema)

/**
 * Details about a sibling variant conflict - which internal product has which variant SKUs.
 */
export const SiblingConflictDetailSchema = z.object({
  productId: z.number().int(),
  productName: z.string(),
  matchedVariantSkus: z.array(z.string()),
})

export type SiblingConflictDetail = z.infer<typeof SiblingConflictDetailSchema>

/**
 * Shopify product variant preview item.
 */
export const ShopifyVariantPreviewItemSchema = z.object({
  variantId: z.string(),
  title: z.string(),
  sku: z.string().nullable(),
  imageUrl: z.string().nullable().optional(),
  cost: z.number().nullable().optional(),
  costCurrency: z.string().nullable().optional(),
  alreadyExists: z.boolean(),
})

export type ShopifyVariantPreviewItem = z.infer<typeof ShopifyVariantPreviewItemSchema>

/**
 * Shopify product preview item for import.
 */
export const ShopifyProductPreviewItemSchema = z.object({
  shopifyProductId: z.string(),
  productName: z.string(),
  handle: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  variantCount: z.number().int(),
  allVariantsExist: z.boolean(),
  hasOnlyDefaultVariant: z.boolean().optional(),
  variants: z.array(ShopifyVariantPreviewItemSchema).nullable().optional(),
  siblingConflict: z.boolean().optional(),
  siblingConflictDetails: z.array(SiblingConflictDetailSchema).nullable().optional(),
})

export type ShopifyProductPreviewItem = z.infer<typeof ShopifyProductPreviewItemSchema>

/**
 * Shopify import preview response.
 */
export const ShopifyImportPreviewResponseSchema = z.object({
  totalProducts: z.number().int(),
  alreadyImported: z.number().int(),
  willImport: z.number().int(),
  alreadyImportedProducts: z.array(ShopifyProductPreviewItemSchema),
  newProducts: z.array(ShopifyProductPreviewItemSchema),
})

export type ShopifyImportPreviewResponse = z.infer<typeof ShopifyImportPreviewResponseSchema>

/**
 * Request to import products from Shopify store.
 */
export const ShopifyImportRequestSchema = z.object({
  productIds: z.array(z.string()).optional(),
})

export type ShopifyImportRequest = z.infer<typeof ShopifyImportRequestSchema>

/**
 * Summary of an imported Shopify product.
 */
export const ShopifyImportedProductSchema = z.object({
  shopifyProductId: z.string(),
  productName: z.string(),
  dimProductId: z.string().uuid(),
  variantCount: z.number().int(),
})

export type ShopifyImportedProduct = z.infer<typeof ShopifyImportedProductSchema>

/**
 * Details about a failed product import.
 */
export const ShopifyImportErrorSchema = z.object({
  shopifyProductId: z.string(),
  productName: z.string(),
  errorMessage: z.string(),
})

export type ShopifyImportError = z.infer<typeof ShopifyImportErrorSchema>

/**
 * Result of Shopify product import operation.
 */
export const ShopifyImportResponseSchema = z.object({
  totalFound: z.number().int(),
  alreadyImported: z.number().int(),
  newlyImported: z.number().int(),
  failed: z.number().int(),
  imported: z.array(ShopifyImportedProductSchema),
  errors: z.array(ShopifyImportErrorSchema),
})

export type ShopifyImportResponse = z.infer<typeof ShopifyImportResponseSchema>
